import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import api from '../../services/api'
import toast from 'react-hot-toast'

interface NotificationPreferences {
  email_new_leads: boolean
  email_daily_report: boolean
  email_weekly_report: boolean
  email_replies: boolean
  email_subscription: boolean
}

export default function NotificationSettings() {
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    email_new_leads: true,
    email_daily_report: true,
    email_weekly_report: false,
    email_replies: true,
    email_subscription: true,
  })

  const saveMutation = useMutation({
    mutationFn: async (prefs: NotificationPreferences) => {
      const response = await api.patch('/auth/notifications', prefs)
      return response.data
    },
    onSuccess: () => {
      toast.success('Настройки сохранены')
    },
    onError: () => {
      toast.error('Ошибка при сохранении настроек')
    },
  })

  const handleToggle = (key: keyof NotificationPreferences) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const handleSave = () => {
    saveMutation.mutate(preferences)
  }

  const notifications = [
    {
      key: 'email_new_leads' as const,
      title: 'Новые лиды',
      description: 'Получать уведомления о новых найденных лидах',
    },
    {
      key: 'email_daily_report' as const,
      title: 'Ежедневный отчёт',
      description: 'Получать ежедневный отчёт о работе системы',
    },
    {
      key: 'email_weekly_report' as const,
      title: 'Еженедельный отчёт',
      description: 'Получать еженедельную сводку результатов',
    },
    {
      key: 'email_replies' as const,
      title: 'Ответы на сообщения',
      description: 'Получать уведомления об ответах лидов',
    },
    {
      key: 'email_subscription' as const,
      title: 'Подписка',
      description: 'Уведомления о статусе подписки и платежах',
    },
  ]

  return (
    <div className="space-y-6">
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Email уведомления
        </h2>

        <div className="space-y-4">
          {notifications.map((notification) => (
            <div
              key={notification.key}
              className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0"
            >
              <div>
                <p className="font-medium text-gray-900">{notification.title}</p>
                <p className="text-sm text-gray-500">{notification.description}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences[notification.key]}
                  onChange={() => handleToggle(notification.key)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          ))}
        </div>

        <button
          onClick={handleSave}
          disabled={saveMutation.isPending}
          className="btn-primary mt-6"
        >
          {saveMutation.isPending ? 'Сохранение...' : 'Сохранить настройки'}
        </button>
      </div>

      {/* Telegram Notifications */}
      <div className="card bg-gray-50">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Telegram уведомления
        </h2>
        <p className="text-gray-600 mb-4">
          Telegram уведомления будут доступны в следующем обновлении.
        </p>
        <button
          disabled
          className="px-4 py-2 bg-gray-200 text-gray-500 rounded-lg cursor-not-allowed"
        >
          Скоро
        </button>
      </div>
    </div>
  )
}
